/**
 * Error Monitoring and Telemetry Service
 * Provides client-side error tracking, performance monitoring, and analytics
 * Using PostHog as the single monitoring provider
 */

import React from 'react';
import { featureFlags } from '../../config/api-config';

export interface ErrorContext {
  userId?: string;
  organizationId?: string;
  userAgent?: string;
  url?: string;
  timestamp: Date;
  sessionId?: string;
  buildVersion?: string;
}

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: Date;
  context?: Record<string, any>;
}

export interface UserEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp: Date;
  userId?: string;
  sessionId?: string;
}

export interface MonitoringConfig {
  posthogKey?: string;
  enableErrorTracking: boolean;
  enablePerformanceMonitoring: boolean;
  enableUserAnalytics: boolean;
  environment: string;
  sampleRate: number;
}

class ErrorMonitoringService {
  private config: MonitoringConfig;
  private sessionId: string;
  private isInitialized = false;
  private performanceObserver?: PerformanceObserver;
  private breadcrumbs: Array<{ message: string; category: string; level: string; timestamp: number }> = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.config = this.getMonitoringConfig();
  }

  /**
   * Initialize monitoring services
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize PostHog for analytics and error tracking
      if (this.config.posthogKey && (this.config.enableUserAnalytics || this.config.enableErrorTracking)) {
        await this.initializePosthog();
      }

      // Set up performance monitoring
      this.setupPerformanceMonitoring();

      // Set up global error handlers
      this.setupGlobalErrorHandlers();

      this.isInitialized = true;
      console.log('Error monitoring initialized successfully');

    } catch (error) {
      console.error('Failed to initialize error monitoring:', error);
    }
  }

  /**
   * Report an error
   */
  reportError(error: Error, context?: Partial<ErrorContext>): void {
    if (!this.config.enableErrorTracking) {
      return;
    }

    const errorContext = this.buildErrorContext(context);

    try {
      // Log to console in development
      if (this.config.environment === 'development') {
        console.error('Error reported:', error, errorContext);
      }

      // Send to PostHog as error event
      if (window.posthog) {
        window.posthog.capture('$exception', {
          $exception_message: error.message,
          $exception_type: error.name,
          $exception_stack_trace_raw: error.stack,
          environment: this.config.environment,
          session_id: this.sessionId,
          url: errorContext.url,
          user_agent: errorContext.userAgent,
          user_id: errorContext.userId,
          organization_id: errorContext.organizationId,
          build_version: errorContext.buildVersion,
          breadcrumbs: this.breadcrumbs.slice(-10), // Last 10 breadcrumbs
        });
      }

    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  }

  /**
   * Report a performance metric
   */
  reportPerformanceMetric(metric: PerformanceMetric): void {
    if (!this.config.enablePerformanceMonitoring) {
      return;
    }

    try {
      // Send to PostHog as custom event
      if (window.posthog) {
        window.posthog.capture('performance_metric', {
          metric_name: metric.name,
          metric_value: metric.value,
          metric_unit: metric.unit,
          ...metric.context
        });
      }

      // Log to console in development
      if (this.config.environment === 'development') {
        console.log(`Performance metric: ${metric.name} = ${metric.value}${metric.unit}`);
      }

    } catch (error) {
      console.error('Failed to report performance metric:', error);
    }
  }

  /**
   * Track user event
   */
  trackUserEvent(event: UserEvent): void {
    if (!this.config.enableUserAnalytics) {
      return;
    }

    try {
      const eventData = {
        ...event.properties,
        sessionId: this.sessionId,
        environment: this.config.environment,
        buildVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
      };

      // Send to PostHog
      if (window.posthog) {
        window.posthog.capture(event.name, eventData);
      }

      // Log to console in development
      if (this.config.environment === 'development') {
        console.log('User event tracked:', { name: event.name, properties: eventData });
      }

    } catch (error) {
      console.error('Failed to track user event:', error);
    }
  }

  /**
   * Set user context
   */
  setUserContext(userId: string, properties?: Record<string, any>): void {
    try {
      // Set in PostHog
      if (window.posthog) {
        window.posthog.identify(userId, properties);
      }

    } catch (error) {
      console.error('Failed to set user context:', error);
    }
  }

  /**
   * Add breadcrumb for debugging
   * Breadcrumbs are included with error reports to provide context
   */
  addBreadcrumb(message: string, category: string = 'default', level: 'info' | 'warning' | 'error' = 'info'): void {
    try {
      const breadcrumb = {
        message,
        category,
        level,
        timestamp: Date.now(),
      };

      // Store locally (keep last 50)
      this.breadcrumbs.push(breadcrumb);
      if (this.breadcrumbs.length > 50) {
        this.breadcrumbs.shift();
      }

    } catch (error) {
      console.error('Failed to add breadcrumb:', error);
    }
  }

  /**
   * Clear user context (on logout)
   */
  clearUserContext(): void {
    try {
      if (window.posthog) {
        window.posthog.reset();
      }
      this.breadcrumbs = [];
    } catch (error) {
      console.error('Failed to clear user context:', error);
    }
  }

  // Private methods

  private getMonitoringConfig(): MonitoringConfig {
    return {
      posthogKey: import.meta.env.VITE_POSTHOG_KEY || 'phc_Qaugu6Y9prd0C19tiTd0B3CKdOzaU1Oc2Wm1HeYAPoW',
      enableErrorTracking: featureFlags.enableErrorTracking,
      enablePerformanceMonitoring: featureFlags.enableAnalytics,
      enableUserAnalytics: featureFlags.enableAnalytics,
      environment: import.meta.env.VITE_ENV || 'development',
      sampleRate: 0.1, // 10% sample rate for performance monitoring
    };
  }

  private async initializePosthog(): Promise<void> {
    try {
      const posthog = await import('posthog-js');

      posthog.default.init(this.config.posthogKey!, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-sensitive]',
        },
      });

      window.posthog = posthog.default;

    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  private setupPerformanceMonitoring(): void {
    if (!this.config.enablePerformanceMonitoring) {
      return;
    }

    try {
      // Monitor Core Web Vitals
      if ('PerformanceObserver' in window) {
        this.performanceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'measure') {
              this.reportPerformanceMetric({
                name: entry.name,
                value: entry.duration,
                unit: 'ms',
                timestamp: new Date(),
                context: { entryType: entry.entryType }
              });
            }
          }
        });

        this.performanceObserver.observe({ entryTypes: ['measure'] });
      }

      // Monitor page load performance
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

          if (perfData) {
            this.reportPerformanceMetric({
              name: 'page_load_time',
              value: perfData.loadEventEnd - perfData.fetchStart,
              unit: 'ms',
              timestamp: new Date(),
            });

            this.reportPerformanceMetric({
              name: 'dom_content_loaded',
              value: perfData.domContentLoadedEventEnd - perfData.fetchStart,
              unit: 'ms',
              timestamp: new Date(),
            });
          }
        }, 0);
      });

    } catch (error) {
      console.error('Failed to setup performance monitoring:', error);
    }
  }

  private setupGlobalErrorHandlers(): void {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError(new Error(event.reason || 'Unhandled promise rejection'), {
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    });

    // Catch global errors
    window.addEventListener('error', (event) => {
      this.reportError(event.error || new Error(event.message), {
        url: event.filename,
        userAgent: navigator.userAgent,
      });
    });
  }

  private buildErrorContext(context?: Partial<ErrorContext>): ErrorContext {
    return {
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      buildVersion: process.env.REACT_APP_VERSION || 'unknown',
      ...context,
    };
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton instance
export const errorMonitoringService = new ErrorMonitoringService();

// React hook for error monitoring
export const useErrorMonitoring = () => {
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const initialize = async () => {
      await errorMonitoringService.initialize();
      setIsInitialized(true);
    };

    initialize();
  }, []);

  return {
    isInitialized,
    reportError: errorMonitoringService.reportError.bind(errorMonitoringService),
    reportPerformanceMetric: errorMonitoringService.reportPerformanceMetric.bind(errorMonitoringService),
    trackUserEvent: errorMonitoringService.trackUserEvent.bind(errorMonitoringService),
    setUserContext: errorMonitoringService.setUserContext.bind(errorMonitoringService),
    addBreadcrumb: errorMonitoringService.addBreadcrumb.bind(errorMonitoringService),
    clearUserContext: errorMonitoringService.clearUserContext.bind(errorMonitoringService),
  };
};

// Error Boundary component
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error; resetError: () => void }> },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    errorMonitoringService.reportError(error, {
      userId: undefined, // Would get from auth context
      url: window.location.href,
    });

    errorMonitoringService.addBreadcrumb(
      `React error boundary caught error: ${error.message}`,
      'error',
      'error'
    );
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// Default error fallback component
const DefaultErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({
  error,
  resetError
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg
            className="h-6 w-6 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-lg font-medium text-gray-800">Something went wrong</h3>
          <div className="mt-2 text-sm text-gray-500">
            <p>An unexpected error occurred. The error has been reported automatically.</p>
          </div>
        </div>
      </div>

      {import.meta.env.DEV && (
        <div className="mt-4 p-3 bg-gray-100 rounded text-sm text-gray-700">
          <strong>Error details:</strong>
          <pre className="mt-1 whitespace-pre-wrap">{error.message}</pre>
        </div>
      )}

      <div className="mt-6">
        <button
          onClick={resetError}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Try again
        </button>
      </div>
    </div>
  </div>
);

// Type declarations for global objects
declare global {
  interface Window {
    posthog?: any;
  }
}

export default errorMonitoringService;
